import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { alpha, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import PrintIcon from "@mui/icons-material/Print";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import { motion } from "framer-motion";

import { orange, red, blue, green } from "@mui/material/colors";
import { validateArrayIncludes } from "../../../common/validators";
import { timeMask } from "../../../common/dateMask";
import {
  getChannelLogoPath,
  getIconColor,
  getIconComponent,
} from "../../../utils/merchant";
import {
  ORDER_STATUS_DISPATCHABLE,
  OrderTotalRanking,
} from "../../../types/order";
import {
  getStatusNumberBackgroundColor,
  getStatusNumberIconColor,
  getStatusNumberText,
} from "../../../utils/orders";
import {
  getStatusIcon,
  getStatusNumberIcon,
} from "../../../common/statusIcons";
import {
  addOrderToDispatch,
  removeOrderToDispatch,
} from "../../../store/actions/ordersSlice";

const OrderCard = ({ order, onViewDetails, onAcceptOrder, onPrintOrder }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const ordersToDispatch = useSelector(
    (state) => state.orders.ordersToDispatch
  );

  const [isChecked, setIsChecked] = useState(
    ordersToDispatch.includes(order.order_id)
  );

  useEffect(() => {
    setIsChecked(ordersToDispatch.includes(order.order_id));
  }, [ordersToDispatch, order.order_id]);

  const orderNumber = order.order_index || "0000";
  const orderChannelNumber = order.channel_code || "####";
  const customerName = order.customer_name || "Cliente";
  const rankingByTotal = order.ranking_by_total;

  const isActuallyLate =
    order.lateStatus && validateArrayIncludes([2, 7], order.status_number);
  const isAlmostLate =
    order.isAlmostLate && validateArrayIncludes([2, 7], order.status_number);

  const isDispatchable =
    order.order_type === "DELIVERY" &&
    validateArrayIncludes(ORDER_STATUS_DISPATCHABLE, order.status_number);

  const isSpecialRanking = rankingByTotal === OrderTotalRanking.SPECIAL;
  const isNewOrder = order.status_number === 0;

  const getMerchantFoodTypeIcon = (foodTypeIcon) => {
    return getIconComponent(foodTypeIcon || "RestaurantIcon");
  };

  const getMerchantIconColor = (foodTypeIcon) => {
    return getIconColor(foodTypeIcon || "RestaurantIcon");
  };

  const getChannelMerchantInitials = (channelMerchant) => {
    if (!channelMerchant) return "N/A";
    return channelMerchant
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .join("")
      .substring(0, 2);
  };

  const handleCheckboxChange = useCallback(
    (event) => {
      event.stopPropagation();
      const checked = event.target.checked;
      if (checked) {
        dispatch(addOrderToDispatch(order.order_id));
      } else {
        dispatch(removeOrderToDispatch(order.order_id));
      }
    },
    [dispatch, order.order_id]
  );

  const handleViewDetails = useCallback(() => {
    onViewDetails(order);
  }, [order, onViewDetails]);

  const handleAcceptOrder = useCallback(
    (e) => {
      e.stopPropagation();
      onAcceptOrder(order);
    },
    [order, onAcceptOrder]
  );

  const handlePrintOrder = useCallback(
    async (e) => {
      e.stopPropagation();
      await onPrintOrder(order.order_id, e);
    },
    [order.order_id, onPrintOrder]
  );

  const handleKeyDown = useCallback(
    (event) => {
      event.preventDefault();
      handleViewDetails();
    },
    [handleViewDetails]
  );

  const shouldShowAcceptButton = order.status_number === 0;
  const shouldShowPrintButton = validateArrayIncludes(
    [2, 3, 4, 5, 6, 7],
    order.status_number
  );

  // --- UI Helpers ---

  const createChannelLogo = () => {
    const logoPath = getChannelLogoPath(order.channel);
    if (!logoPath) return null;
    return (
      <Avatar
        src={logoPath}
        sx={{ width: 20, height: 20, borderRadius: "50%" }}
      />
    );
  };

  const createFoodTypeIcon = () => {
    if (!order.food_type_icon) {
      return (
        <Typography
          sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#FFF" }}
        >
          {getChannelMerchantInitials(order.channel_merchant)}
        </Typography>
      );
    }
    return React.createElement(getMerchantFoodTypeIcon(order.food_type_icon), {
      sx: { fontSize: 16, color: "#FFF" },
    });
  };

  const createStatusIcon = () => {
    if (order.status_number) return getStatusNumberIcon(order.status_number);
    return getStatusIcon(order.status);
  };

  // --- Styles & Variants ---

  const cardVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    hover: { 
      y: -5,
      boxShadow: theme.shadows[10],
      transition: { duration: 0.2 } 
    },
    tap: { scale: 0.98 }
  };

  const newOrderVariants = {
    animate: {
      boxShadow: [
        `0 0 0 0 ${alpha(theme.palette.primary.main, 0.4)}`,
        `0 0 0 10px ${alpha(theme.palette.primary.main, 0)}`
      ],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const getCardBackground = () => {
    if (isNewOrder) {
      return `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`;
    }
    if (isChecked) {
      return alpha(theme.palette.primary.main, 0.05);
    }
    if (isActuallyLate) {
      return alpha(red[50], 0.8);
    }
    if (isAlmostLate) {
      return alpha(orange[50], 0.8);
    }
    return theme.palette.background.paper;
  };

  const getBorderColor = () => {
    if (isChecked) return theme.palette.primary.main;
    if (isNewOrder) return theme.palette.primary.light;
    if (isActuallyLate) return red[300];
    if (isAlmostLate) return orange[300];
    return "transparent";
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="initial"
      animate="animate"
      whileHover="hover"
      whileTap="tap"
      style={{ height: "100%" }}
    >
      <Card
        elevation={isChecked ? 4 : 1}
        sx={{
          height: "100%",
          borderRadius: 3,
          position: "relative",
          background: getCardBackground(),
          border: `2px solid ${getBorderColor()}`,
          overflow: "visible", // Allow badges to pop out if needed
          ...(isNewOrder && {
            borderColor: theme.palette.primary.main,
            // We use motion.div for the pulse, so standard CSS animation is removed
          })
        }}
        component={isNewOrder ? motion.div : "div"}
        variants={isNewOrder ? newOrderVariants : {}}
      >
        {/* Selection Checkbox */}
        {isDispatchable && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 10,
              bgcolor: "background.paper",
              borderRadius: "50%",
              boxShadow: 1
            }}
          >
            <Checkbox
              checked={isChecked}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              size="small"
              color="primary"
            />
          </Box>
        )}

        {/* Special Ranking Crown */}
        {isSpecialRanking && (
          <Box
            sx={{
              position: "absolute",
              top: -10,
              right: 20,
              zIndex: 10,
              bgcolor: "#FFD700",
              color: "#B8860B",
              borderRadius: "50%",
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: 2
            }}
          >
            <WorkspacePremiumIcon sx={{ fontSize: 18 }} />
          </Box>
        )}

        <CardActionArea
          onClick={handleViewDetails}
          onKeyDown={handleKeyDown}
          sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch", p: 0 }}
        >
          <CardContent sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            
            {/* Header: Order # and Status */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }}>
               {/* Spacer for checkbox */}
               <Box sx={{ width: 32 }} /> 
               
               <Chip 
                  label={getStatusNumberText(order.status_number)} 
                  size="small"
                  icon={createStatusIcon()}
                  sx={{ 
                    bgcolor: getStatusNumberBackgroundColor(order.status_number),
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: 24,
                    "& .MuiChip-icon": { color: "#fff" }
                  }}
               />
            </Box>

            {/* Main Info: Number & Customer */}
            <Box sx={{ textAlign: "center", my: 1 }}>
               <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.text.primary, lineHeight: 1 }}>
                  {orderNumber}
               </Typography>
               <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                  ID: {orderChannelNumber}
               </Typography>
            </Box>

            <Box sx={{ textAlign: "center", flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: theme.palette.text.secondary }}>
                  <PersonIcon fontSize="small" sx={{ opacity: 0.6 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {customerName}
                  </Typography>
                </Box>
                
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                   {createChannelLogo()}
                   <Chip
                      icon={<AccessTimeIcon sx={{ fontSize: "1rem !important" }} />}
                      label={timeMask(order.delivery_expected_time)}
                      size="small"
                      variant="outlined"
                      color={isActuallyLate ? "error" : isAlmostLate ? "warning" : "default"}
                      sx={{ height: 24, fontWeight: 500, fontSize: "0.75rem" }}
                   />
                </Box>
            </Box>

            {/* Footer Actions */}
            {(shouldShowAcceptButton || shouldShowPrintButton) && (
              <Stack direction="row" spacing={1} sx={{ mt: "auto", pt: 1 }}>
                {shouldShowAcceptButton && (
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    size="small"
                    startIcon={<ThumbUpAltIcon />}
                    onClick={handleAcceptOrder}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, boxShadow: 0 }}
                  >
                    Aceitar
                  </Button>
                )}
                {shouldShowPrintButton && (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    size="small"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintOrder}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                  >
                    Imprimir
                  </Button>
                )}
              </Stack>
            )}

            {/* Merchant / Food Icon Badge */}
             <Box sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: getMerchantIconColor(order.food_type_icon),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0.8
             }}>
                {createFoodTypeIcon()}
             </Box>

          </CardContent>
        </CardActionArea>
      </Card>
    </motion.div>
  );
};

export default OrderCard;
